import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get } from '../../../../api/client';
import type { CompareData, PlanItem, ChannelItem, OrderItem } from './types';

/** 会员面板数据查询键（统一命名，便于失效刷新） */
export const MEMBERSHIP_KEYS = {
  compare: ['membership', 'compare'] as const,
  plans: ['membership', 'plans'] as const,
  channels: ['membership', 'channels'] as const,
  orders: ['membership', 'orders'] as const,
};

/**
 * 会员面板数据聚合 hook：对比矩阵 / 套餐 / 支付渠道 / 升级订单。
 * 会员「状态」（等级/到期/生效功能）不在此处，统一走 membershipStore（GET /membership/status），
 * 保证权限判定与展示同源。
 */
export function useMembershipData() {
  const qc = useQueryClient();

  const compare = useQuery({
    queryKey: MEMBERSHIP_KEYS.compare,
    queryFn: () => get<CompareData>('/membership/levels/compare'),
    staleTime: 60_000,
  });

  const plans = useQuery({
    queryKey: MEMBERSHIP_KEYS.plans,
    queryFn: () => get<PlanItem[]>('/payment/plans'),
    staleTime: 60_000,
  });

  const channels = useQuery({
    queryKey: MEMBERSHIP_KEYS.channels,
    queryFn: () => get<ChannelItem[]>('/payment/channels'),
    staleTime: 60_000,
  });

  const orders = useQuery({
    queryKey: MEMBERSHIP_KEYS.orders,
    queryFn: () => get<OrderItem[]>('/payment/orders'),
    staleTime: 30_000,
  });

  const isLoading = compare.isLoading || plans.isLoading;

  /** 支付成功/下单后刷新订单、渠道与会员状态相关数据 */
  const refetchAfterPayment = () => {
    void qc.invalidateQueries({ queryKey: MEMBERSHIP_KEYS.orders });
    void qc.invalidateQueries({ queryKey: MEMBERSHIP_KEYS.channels });
  };

  return {
    compareData: compare.data ?? null,
    plans: plans.data ?? [],
    /** 套餐请求失败（区别于后台确实没有配置任何在售套餐） */
    plansFailed: plans.isError,
    channels: channels.data ?? [],
    orders: orders.data ?? [],
    isLoading,
    refetchAfterPayment,
  };
}
